import { useEffect, useRef, useState } from 'react';
import { GearSix } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Slider } from '~/components/ui/slider';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useProBuilderStatus } from '~/hooks/queries/use-pro-builder';
import { useSaveProBuilderAutoCompact } from '~/hooks/mutations/use-pro-builder';
import {
  AUTO_COMPACT_MAX_POSITION,
  autoCompactHeading,
  autoCompactPanelState,
  autoCompactPosition,
  autoCompactValue,
} from './auto-compact';
import {
  PRO_BUILDER_AUTO_COMPACT_LEFT,
  PRO_BUILDER_AUTO_COMPACT_LOAD_FAILED,
  PRO_BUILDER_AUTO_COMPACT_LOADING,
  PRO_BUILDER_AUTO_COMPACT_RESTART_BUTTON,
  PRO_BUILDER_AUTO_COMPACT_RESTART_NOTE,
  PRO_BUILDER_AUTO_COMPACT_RIGHT,
  PRO_BUILDER_AUTO_COMPACT_SAVE_FAILED,
  PRO_BUILDER_AUTO_COMPACT_SLIDER_LABEL,
  PRO_BUILDER_SETTINGS_LABEL,
} from './pro-builder-copy';
import { useProBuilderSessionStore } from './pro-builder-session-store';

/**
 * FRONT-3228 — the popover body: heading, slider with its two edge labels, and
 * (only while a restart is owed) the restart row. Props only, so the header's
 * connected wrapper below is the one place that touches the status read, the
 * save and the session store.
 */
export function ProBuilderClaudeSettingsPanel({
  position,
  onPositionChange,
  onCommit,
  sliderDisabled,
  notice,
  restartOwed,
  onRestart,
}: {
  position: number;
  onPositionChange: (position: number) => void;
  onCommit: (position: number) => void;
  sliderDisabled: boolean;
  notice: 'loading' | 'error' | null;
  restartOwed: boolean;
  onRestart: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 p-1.5">
      {/* While nothing is loaded the heading would name a value nobody saved. */}
      {notice === null && (
        <h3 data-testid="pro-builder-auto-compact-heading" className="text-sm font-medium text-foreground">
          {autoCompactHeading(position)}
        </h3>
      )}
      <div className="flex flex-col gap-1.5">
        <Slider
          min={0}
          max={AUTO_COMPACT_MAX_POSITION}
          step={1}
          value={[position]}
          disabled={sliderDisabled}
          thumbProps={{
            'aria-label': PRO_BUILDER_AUTO_COMPACT_SLIDER_LABEL,
            'aria-valuetext': autoCompactValue(position) === null ? PRO_BUILDER_AUTO_COMPACT_RIGHT : `${autoCompactValue(position)}%`,
          }}
          data-testid="pro-builder-auto-compact-slider"
          onValueChange={(v) => onPositionChange(v[0] ?? position)}
          onValueCommit={(v) => onCommit(v[0] ?? position)}
        />
        <div className="flex justify-between text-xs text-muted-foreground" aria-hidden>
          <span>{PRO_BUILDER_AUTO_COMPACT_LEFT}</span>
          <span>{PRO_BUILDER_AUTO_COMPACT_RIGHT}</span>
        </div>
      </div>
      {notice !== null && (
        <p data-testid="pro-builder-auto-compact-notice" className="text-xs text-muted-foreground">
          {notice === 'error' ? PRO_BUILDER_AUTO_COMPACT_LOAD_FAILED : PRO_BUILDER_AUTO_COMPACT_LOADING}
        </p>
      )}
      {restartOwed && (
        <div data-testid="pro-builder-auto-compact-restart-row" className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{PRO_BUILDER_AUTO_COMPACT_RESTART_NOTE}</span>
          <Button variant="outline" size="xs" data-testid="pro-builder-auto-compact-restart" onClick={onRestart}>
            {PRO_BUILDER_AUTO_COMPACT_RESTART_BUTTON}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The header's gear. The tooltip composes the raw primitives around the popover
 * trigger (never `Tip`, which would swallow the trigger's cloned props). The
 * slider starts from the saved value each time the popover opens; moving it
 * updates the heading from local state, and only a commit saves. A failed save
 * puts the slider back on the last saved value.
 */
export function ProBuilderClaudeSettings() {
  const status = useProBuilderStatus();
  const save = useSaveProBuilderAutoCompact();
  const startedWith = useProBuilderSessionStore((s) => s.session.startedWith);
  const dispatch = useProBuilderSessionStore((s) => s.dispatch);
  const saved = status.data?.autoCompactPct ?? null;
  const panelState = autoCompactPanelState({
    hasData: status.data !== undefined,
    isError: status.isError,
    startedWith,
    saved,
  });
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(() => autoCompactPosition(saved));
  const hasData = status.data !== undefined;
  // Whether the slider has been synced from a LOADED saved value yet. Opened
  // while the status read was still loading, the slider sat locked on Default;
  // the first data must still move it to the saved value, even while open.
  const syncedWithData = useRef(hasData);
  // While closed the slider mirrors the saved value, so the next open starts from it.
  useEffect(() => {
    if (!open || !syncedWithData.current) setPosition(autoCompactPosition(saved));
    if (hasData) syncedWithData.current = true;
  }, [open, saved, hasData]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={PRO_BUILDER_SETTINGS_LABEL}
              data-testid="pro-builder-settings-menu"
              className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <GearSix size={18} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{PRO_BUILDER_SETTINGS_LABEL}</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-64" data-testid="pro-builder-settings-popover">
        <ProBuilderClaudeSettingsPanel
          position={position}
          onPositionChange={setPosition}
          onCommit={(p) =>
            save.mutate(autoCompactValue(p), {
              onError: () => {
                setPosition(autoCompactPosition(savedRef.current));
                toast.error(PRO_BUILDER_AUTO_COMPACT_SAVE_FAILED);
              },
            })
          }
          sliderDisabled={panelState.sliderDisabled}
          notice={panelState.notice}
          restartOwed={panelState.showRestartRow}
          onRestart={() => dispatch({ type: 'restart' })}
        />
      </PopoverContent>
    </Popover>
  );
}
